import React, { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import MicRoundedIcon from '@mui/icons-material/MicRounded';
import { orchard } from '../../theme';
import useHoldToTalk from './useHoldToTalk';
import { canRecord } from './recorder';

/* Dictation anywhere (computers): hold the RIGHT Ctrl key and speak — on
   release the words are typed into the field that has the focus (or copied,
   when no field has it). Ctrl used for a shortcut (Ctrl+C …) never starts it:
   recording begins only after the key is held alone for a moment.
   A page can take the key over (the talk page sends speech to the assistant):
   setHoldOverride({ press, release }) / setHoldOverride(null). */

let override = null;
export function setHoldOverride(handlers) {
  override = handlers;
}

const HOLD_MS = 260;

const editable = (el) =>
  !!el &&
  ((el.tagName === 'TEXTAREA' && !el.readOnly && !el.disabled) ||
    (el.tagName === 'INPUT' &&
      !el.readOnly &&
      !el.disabled &&
      /^(text|search|email|url|tel|)$/i.test(el.type || '')) ||
    el.isContentEditable);

export function insertText(el, text) {
  if (!el || !text) return false;
  el.focus();
  const value = el.isContentEditable ? el.textContent || '' : el.value || '';
  const start = el.isContentEditable
    ? value.length
    : el.selectionStart != null
      ? el.selectionStart
      : value.length;
  const before = value.slice(0, start);
  const piece = (before && !/\s$/.test(before) ? ' ' : '') + text;
  // execCommand keeps undo (Ctrl+Z) and React's onChange working
  let ok = false;
  try {
    ok = document.execCommand('insertText', false, piece);
  } catch (e) {
    ok = false;
  }
  if (ok) return true;
  if (el.isContentEditable) {
    el.textContent = value + piece;
  } else {
    const proto =
      el.tagName === 'TEXTAREA'
        ? window.HTMLTextAreaElement.prototype
        : window.HTMLInputElement.prototype;
    const setter = Object.getOwnPropertyDescriptor(proto, 'value').set;
    const end = el.selectionEnd != null ? el.selectionEnd : start;
    setter.call(el, before + piece + value.slice(end));
    const caret = (before + piece).length;
    try {
      el.setSelectionRange(caret, caret);
    } catch (e) {
      // some inputs have no selection
    }
  }
  el.dispatchEvent(new Event('input', { bubbles: true }));
  return true;
}

export default function DictationHotkey({ en }) {
  const target = useRef(null);
  const [note, setNote] = useState(null); // { text, kind }
  const noteTimer = useRef(0);
  const show = (text, kind = 'info', ms = 2600) => {
    clearTimeout(noteTimer.current);
    setNote({ text, kind });
    if (ms) noteTimer.current = setTimeout(() => setNote(null), ms);
  };

  const talk = useHoldToTalk({
    onText: (text) => {
      if (!text) {
        show(en ? 'Didn’t catch that' : 'Не расслышала', 'info', 1600);
        return;
      }
      const el = target.current;
      if (el && document.contains(el) && insertText(el, text)) {
        setNote(null);
        return;
      }
      try {
        navigator.clipboard.writeText(text);
        show(
          `${en ? 'Copied' : 'Скопировано'}: «${text.slice(0, 80)}${text.length > 80 ? '…' : ''}»`,
          'info',
          4000
        );
      } catch (e) {
        show(text, 'info', 6000);
      }
    },
    onError: (e) => show(e.message, 'error', 4000),
  });
  const talkRef = useRef(talk);
  talkRef.current = talk;
  const levelRef = useRef(null);

  useEffect(() => {
    if (!canRecord()) return undefined;
    let timer = 0;
    let armed = false; // right Ctrl is down and nothing else was pressed
    let active = false; // this press started a recording (or an override)
    let via = null;

    const down = (e) => {
      if (e.code === 'ControlRight') {
        if (e.repeat || armed || active) return;
        armed = true;
        timer = setTimeout(() => {
          if (!armed) return;
          active = true;
          if (override) {
            via = override;
            via.press();
            return;
          }
          via = null;
          const el = document.activeElement;
          target.current = editable(el) ? el : null;
          talkRef.current.press();
        }, HOLD_MS);
        return;
      }
      // any other key while Ctrl is held: it was a shortcut, not dictation
      if (armed && !active) {
        armed = false;
        clearTimeout(timer);
      }
    };
    const up = (e) => {
      if (e.code !== 'ControlRight') return;
      armed = false;
      clearTimeout(timer);
      if (!active) return;
      active = false;
      if (via) via.release();
      else talkRef.current.release();
      via = null;
    };
    const blur = () => {
      armed = false;
      clearTimeout(timer);
      if (active && !via) talkRef.current.release();
      if (active && via) via.release();
      active = false;
      via = null;
    };
    window.addEventListener('keydown', down, true);
    window.addEventListener('keyup', up, true);
    window.addEventListener('blur', blur);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('keydown', down, true);
      window.removeEventListener('keyup', up, true);
      window.removeEventListener('blur', blur);
    };
  }, []);

  const { state, level } = talk;
  const live = state === 'recording' || state === 'starting';

  useEffect(() => {
    if (!live) return undefined;
    let raf = 0;
    let smooth = 0;
    const tick = () => {
      smooth += (level() - smooth) * 0.3;
      const bars = levelRef.current ? levelRef.current.children : [];
      for (let i = 0; i < bars.length; i++) {
        const k =
          0.35 + 0.65 * Math.abs(Math.sin(performance.now() / 160 + i * 1.3));
        bars[i].style.transform = `scaleY(${0.15 + smooth * k * 1.2})`;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [live, level]);

  if (!live && state !== 'transcribing' && !note) return null;

  return (
    <Box
      role="status"
      sx={{
        position: 'fixed',
        left: '50%',
        bottom: 'max(24px, env(safe-area-inset-bottom))',
        transform: 'translateX(-50%)',
        zIndex: 2000,
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        px: 2,
        py: 1.1,
        maxWidth: 'calc(100vw - 32px)',
        borderRadius: 999,
        color: '#fff',
        bgcolor: (t) =>
          note && note.kind === 'error'
            ? t.palette.error.main
            : alpha('#1d1013', 0.92),
        boxShadow: '0 18px 40px -16px rgba(0,0,0,0.55)',
        pointerEvents: 'none',
      }}
    >
      {live ? (
        <>
          <Box
            sx={{
              width: 26,
              height: 26,
              borderRadius: '50%',
              bgcolor: orchard.cherry,
              display: 'grid',
              placeItems: 'center',
              flexShrink: 0,
            }}
          >
            <MicRoundedIcon sx={{ fontSize: 17 }} />
          </Box>
          <Box
            ref={levelRef}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '3px',
              height: 22,
            }}
          >
            {[0, 1, 2, 3, 4].map((i) => (
              <Box
                key={i}
                sx={{
                  width: 4,
                  height: 22,
                  borderRadius: 2,
                  bgcolor: '#fff',
                  transformOrigin: 'center',
                  transform: 'scaleY(0.15)',
                }}
              />
            ))}
          </Box>
          <Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }} noWrap>
            {talk.latched
              ? en
                ? 'Listening… press right Ctrl to finish'
                : 'Слушаю… правый Ctrl — закончить'
              : en
                ? 'Listening… release right Ctrl to insert'
                : 'Слушаю… отпустите правый Ctrl — вставлю текст'}
          </Typography>
        </>
      ) : state === 'transcribing' ? (
        <>
          <CircularProgress size={18} sx={{ color: '#fff' }} />
          <Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }}>
            {en ? 'Writing it down…' : 'Записываю…'}
          </Typography>
        </>
      ) : (
        <Typography sx={{ fontWeight: 600, fontSize: '0.88rem' }}>
          {note.text}
        </Typography>
      )}
    </Box>
  );
}
